import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import './BunkTrailersPage.css'

interface TrailerModel {
  model: string;
  capacity: number;
  boatLength: string;
  overallLength: string;
  bowEyeMin?: string;
  bowEyeMax?: string;
  gvwr: number;
  tongueJack: string;
  axles: number;
  brakes: string;
  tireSize: string;
  overallWidth: string;
  betweenFenders: string;
}

const BunkTrailersPage: React.FC = () => {
  // Aluminum Bunk Trailers
  const aluminumSingleAxleModels: TrailerModel[] = [
    {
      model: "VAB-1025",
      capacity: 1025,
      boatLength: "12'-14'",
      overallLength: "15'",
      bowEyeMin: "8'2\"",
      bowEyeMax: "12'8\"",
      gvwr: 1230,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VAB-2825",
      capacity: 2825,
      boatLength: "18'-20'",
      overallLength: "22'1\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 3525,
      tongueJack: "Optional",
      axles: 1,
      brakes: "Optional",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VAB-3525",
      capacity: 3525,
      boatLength: "20'-22'",
      overallLength: "24'",
      bowEyeMin: "17'6\"",
      bowEyeMax: "21'",
      gvwr: 4350,
      tongueJack: "Single Wheel",
      axles: 1,
      brakes: "Disc",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  const aluminumTandemAxleModels: TrailerModel[] = [
    {
      model: "VATB-3625",
      capacity: 3625,
      boatLength: "18'-20'",
      overallLength: "22'10\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 4585,
      tongueJack: "Single Wheel",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VATB-6425",
      capacity: 6425,
      boatLength: "24'-25'",
      overallLength: "27'7\"",
      bowEyeMin: "19'",
      bowEyeMax: "24'",
      gvwr: 7555,
      tongueJack: "2000lb. Foot",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VATB-8725",
      capacity: 8725,
      boatLength: "27'-29'",
      overallLength: "30'9\"",
      bowEyeMin: "21'6\"",
      bowEyeMax: "26'",
      gvwr: 10085,
      tongueJack: "2000lb. Foot",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  const aluminumTripleAxleModels: TrailerModel[] = [
    {
      model: "VATB-9625",
      capacity: 9625,
      boatLength: "27'-29'",
      overallLength: "31'7\"",
      bowEyeMin: "22'",
      bowEyeMax: "28'",
      gvwr: 11425,
      tongueJack: "2000lb. Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VATB-15025",
      capacity: 15025,
      boatLength: "32'-36'",
      overallLength: "39'11\"",
      bowEyeMin: "30'",
      bowEyeMax: "35'",
      gvwr: 17505,
      tongueJack: "2000lb. Foot",
      axles: 3,
      brakes: "Disc-3",
      tireSize: "ST235/80R 16 LRE 8H",
      overallWidth: "102\"",
      betweenFenders: "82\""
    }
  ];

  // Galvanized Bunk Trailers
  const galvanizedSingleAxleModels: TrailerModel[] = [
    {
      model: "VB-1000",
      capacity: 1000,
      boatLength: "12'-14'",
      overallLength: "15'9\"",
      bowEyeMin: "8'6\"",
      bowEyeMax: "13'",
      gvwr: 1270,
      tongueJack: "Optional",
      axles: 1,
      brakes: "No",
      tireSize: "4.80 x 12 LRC 5H",
      overallWidth: "60\"",
      betweenFenders: "42\""
    },
    {
      model: "VB-2800",
      capacity: 2800,
      boatLength: "20'-21'",
      overallLength: "22'11\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 3625,
      tongueJack: "Optional",
      axles: 1,
      brakes: "Optional",
      tireSize: "ST205/75R 15 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VB-3300",
      capacity: 3300,
      boatLength: "20'-21'",
      overallLength: "23'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 4175,
      tongueJack: "Single Wheel",
      axles: 1,
      brakes: "Disc",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  const galvanizedTandemAxleModels: TrailerModel[] = [
    {
      model: "VBT-3600S",
      capacity: 3600,
      boatLength: "18'-20'",
      overallLength: "22'6\"",
      bowEyeMin: "15'",
      bowEyeMax: "19'",
      gvwr: 4600,
      tongueJack: "Single Wheel",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VBT-5900",
      capacity: 5900,
      boatLength: "24'-25'",
      overallLength: "27'6\"",
      bowEyeMin: "19'",
      bowEyeMax: "24'",
      gvwr: 7300,
      tongueJack: "2000lb. Foot",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST215/75R 15 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VBT-7900",
      capacity: 7900,
      boatLength: "25'-27'",
      overallLength: "29'10\"",
      bowEyeMin: "20'",
      bowEyeMax: "25'",
      gvwr: 9350,
      tongueJack: "2000lb. Foot",
      axles: 2,
      brakes: "Disc-1",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  const galvanizedTripleAxleModels: TrailerModel[] = [
    {
      model: "VBT-8600",
      capacity: 8600,
      boatLength: "27'-28'",
      overallLength: "31'6\"",
      bowEyeMin: "22'",
      bowEyeMax: "26'6\"",
      gvwr: 10420,
      tongueJack: "2000lb. Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    },
    {
      model: "VBT-10800",
      capacity: 10800,
      boatLength: "29'-31'",
      overallLength: "32'11\"",
      bowEyeMin: "22'",
      bowEyeMax: "28'",
      gvwr: 12640,
      tongueJack: "2000lb. Foot",
      axles: 3,
      brakes: "Disc-2",
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
      betweenFenders: "81\""
    }
  ];

  const bunkTrailerFeatures = [
    "Plug & Play Lights with weatherproof connections",
    "Aluminum Diamond Plate Fenders for enhanced durability",
    "Heavy Duty Winch Straps for secure boat transport",
    "D.O.T. Composite Brake Line with Brass Fittings",
    "Balanced Radial Tires for smooth highway towing",
    "Eliminator GalvX Vented Rotor Disc Brakes",
    "Super Lube Spindles for reduced maintenance",
    "Professional Wheel Balancing included",
    "Marine-grade carpeted bunks for hull protection",
    "Adjustable bunk positioning for perfect fit",
    "Galvanized Hardware, U-bolts, Winch Stand, Axles, Tongue",
    "Multiple axle configurations for various boat sizes"
  ];

  return (
    <div className="bunk-page">
      <SEOHead
        title="Bunk Boat Trailers - Gentle Hull Support | Fast Boat Trailers"
        description="Bunk boat trailers from Fast Boat Trailers in Edgewater, MD. Carpeted bunk support protects your hull with even weight distribution. Aluminum and galvanized options for all boat sizes."
        keywords="bunk boat trailers, carpeted bunk trailers, hull protection trailers, bunk trailers Maryland, aluminum bunk trailers, galvanized bunk trailers"
        ogTitle="Bunk Boat Trailers - Gentle Hull Support"
        ogDescription="Bunk boat trailers with carpeted support for gentle hull protection. Even weight distribution for aluminum, fiberglass, and wooden hulls."
        ogImage="/images/trailers/aluminum-single-axle-bunk.jpg"
        canonical="https://fastboattrailers.com/trailers/bunk"
      />
      <div className="container">
        {/* Hero Section */}
        <div className="bunk-hero">
          <div className="hero-main">
            <div className="hero-content">
              <h1>Bunk Trailers</h1>
              <h2>Low Maintenance, High Performance</h2>
              <p>
                Bunk Trailers are great for those looking for a low maintenance option that makes it easy to store over time. 
                Bunk trailers are more affordable than roller trailers due to simpler design and fewer moving parts. 
                Most bunk trailers offer drive-on capability since the trailer must be relatively deep in order to launch and load your boat.
              </p>
              
              <h3>Benefits of Bunk Boat Trailers</h3>
              <ul className="benefits-list">
                <li>Best for boat storage</li>
                <li>Less maintenance required</li>
                <li>Drive-on capability</li>
              </ul>
              
            </div>
            <div className="hero-features">
              <h3>Standard Features</h3>
              <div className="features-compact">
                {bunkTrailerFeatures.map((feature, index) => (
                  <div key={index} className="feature-item-compact">
                    <div className="feature-icon-compact">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Aluminum Bunk Trailers Section */}
        <div className="models-section">
          <h2>Aluminum Bunk Trailers</h2>
          
          {/* Single Axle Aluminum Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/bunk-aluminum-single-axle.jpg" alt="Aluminum Single Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Single Axle Aluminum Bunk Models</h3>
              <p>Lightweight aluminum construction for smaller boats with excellent corrosion resistance.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumSingleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tandem Axle Aluminum Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/bunk-aluminum-tandem-axle.jpg" alt="Aluminum Tandem Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Tandem Axle Aluminum Bunk Models</h3>
              <p>Enhanced load distribution and aluminum durability for mid-size boats.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumTandemAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Triple Axle Aluminum Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/bunk-aluminum-triple-axle.jpg" alt="Aluminum Triple Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Triple Axle Aluminum Bunk Models</h3>
              <p>Maximum aluminum strength for the largest boats requiring premium support.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumTripleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Galvanized Bunk Trailers Section */}
        <div className="models-section">
          <h2>Galvanized Bunk Trailers</h2>
          
          {/* Single Axle Galvanized Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/bunk-galvanized-single-axle.jpg" alt="Galvanized Single Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Single Axle Galvanized Bunk Models</h3>
              <p>Hot-dip galvanized protection for maximum corrosion resistance in marine environments.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {galvanizedSingleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tandem Axle Galvanized Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/bunk-galvanized-tandem-axle.jpg" alt="Galvanized Tandem Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Tandem Axle Galvanized Bunk Models</h3>
              <p>Superior galvanized protection with enhanced stability for larger boats.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {galvanizedTandemAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Triple Axle Galvanized Models */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/bunk-galvanized-triple-axle.jpg" alt="Galvanized Triple Axle Bunk Trailer" />
            </div>
            <div className="category-header">
              <h3>Triple Axle Galvanized Bunk Models</h3>
              <p>Maximum galvanized protection and capacity for the most demanding marine applications.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>GVWR</th>
                    <th>Brakes</th>
                    <th>Tongue Jack</th>
                    <th>Tires</th>
                  </tr>
                </thead>
                <tbody>
                  {galvanizedTripleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()} lbs</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.gvwr.toLocaleString()} lbs</td>
                      <td>{model.brakes}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.tireSize}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bunk-cta">
          <h2>Ready for a Bunk Trailer?</h2>
          <p>
            Choose the reliability and affordability of bunk trailers. With excellent hull support, low maintenance requirements, 
            and drive-on convenience, bunk trailers offer the perfect combination of value and performance for your boating needs.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Get a quote</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BunkTrailersPage